import { supabase } from '../supabaseClient';

/**
 * 取得使用者的通知（分頁）
 */
export const getNotifications = async (userId, { limit = 20, offset = 0 } = {}) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select(`
        *,
        actor:actor_id (id, email)
      `)
      .eq('user_id', userId)
      .eq('is_read', false)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return { data: null, error };
  }
};

/**
 * 取得未讀通知數量
 */
export const getUnreadCount = async (userId) => {
  try {
    const { count, error } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_read', false);

    if (error) throw error;
    return { count: count || 0, error: null };
  } catch (error) {
    console.error('Error fetching unread count:', error);
    return { count: 0, error };
  }
};

/**
 * 標記通知為已讀
 */
export const markAsRead = async (notificationId) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({
        is_read: true,
        read_at: new Date().toISOString(),
      })
      .eq('id', notificationId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return { error };
  }
};

/**
 * 標記所有通知為已讀
 */
export const markAllAsRead = async (userId) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({
        is_read: true,
        read_at: new Date().toISOString(),
      })
      .eq('user_id', userId)
      .eq('is_read', false);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error marking all as read:', error);
    return { error };
  }
};

/**
 * 建立通知
 */
export const createNotification = async (data) => {
  try {
    const insertData = {
      user_id: data.user_id,
      type: data.type,
      title: data.title,
      message: data.message || null,
      resource_type: data.resource_type || null,
      resource_id: data.resource_id || null,
      actor_id: data.actor_id || null,
    };

    const { data: notification, error } = await supabase
      .from('notifications')
      .insert(insertData)
      .select()
      .single();

    if (error) throw error;
    return { data: notification, error: null };
  } catch (error) {
    console.error('Error creating notification:', error);
    return { data: null, error };
  }
};

/**
 * 批次建立通知（例如 Ticket 狀態變更通知所有 assignees）
 */
export const createBatchNotifications = async (notifications) => {
  try {
    if (!notifications || notifications.length === 0) return { error: null };

    const { error } = await supabase
      .from('notifications')
      .insert(notifications);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error creating batch notifications:', error);
    return { error };
  }
};

/**
 * 建立 Email HTML 模板
 * @param {'ticket_assigned' | 'mention'} type - 通知類型
 * @param {object} options
 * @param {string} options.title - 通知標題
 * @param {string} options.message - 內文摘要
 * @param {string} options.ticketId - Ticket ID
 * @param {string} [options.ticketLabel] - Ticket 顯示名稱
 * @param {string} [options.url] - 覆寫「查看詳情」連結(工作項目等非 Ticket 資源用)
 */
export const buildEmailHtml = ({ title, message, ticketId, ticketLabel, url }) => {
  const mainSystemUrl = import.meta.env.VITE_MAIN_SYSTEM_URL || '';
  const ticketUrl = url || `${mainSystemUrl}/project/#/ticket?id=${ticketId}`;

  return `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="margin:0;padding:0;background:#f4f5f7;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f5f7;padding:32px 0;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;">
        <tr>
          <td style="background:#1976d2;padding:20px 24px;">
            <span style="color:#ffffff;font-size:18px;font-weight:600;">${title}</span>
          </td>
        </tr>
        <tr>
          <td style="padding:24px;">
            ${ticketLabel ? `<p style="margin:0 0 12px;color:#333;font-size:15px;font-weight:600;">${ticketLabel}</p>` : ''}
            ${message ? `<p style="margin:0 0 20px;color:#555;font-size:14px;line-height:1.6;">${message}</p>` : ''}
            <a href="${ticketUrl}" style="display:inline-block;background:#1976d2;color:#ffffff;text-decoration:none;padding:10px 24px;border-radius:4px;font-size:14px;font-weight:500;">查看詳情</a>
          </td>
        </tr>
        <tr>
          <td style="padding:16px 24px;border-top:1px solid #eee;">
            <p style="margin:0;color:#999;font-size:12px;">此為系統自動發送的通知信件，請勿直接回覆。</p>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`.trim();
};

/**
 * 建立 Teams Adaptive Card payload
 */
export const buildTeamsCard = ({ title, message, url, targetEmail }) => ({
  type: 'AdaptiveCard',
  $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
  version: '1.4',
  summary: message ? `${title} - ${message}` : title,
  ...(targetEmail ? { targetEmail } : {}),
  body: [
    { type: 'TextBlock', text: '【Sonice 通知】', weight: 'Bolder', size: 'Medium' },
    { type: 'TextBlock', text: title, wrap: true, weight: 'Bolder' },
    ...(message ? [{ type: 'TextBlock', text: message, wrap: true }] : []),
    ...(targetEmail ? [{ type: 'TextBlock', text: `👤 ${targetEmail}`, size: 'Small', isSubtle: true }] : []),
    { type: 'TextBlock', text: `⏰ ${new Date().toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })}`, size: 'Small', isSubtle: true },
  ],
  ...(url ? { actions: [{ type: 'Action.OpenUrl', title: '查看詳情', url }] } : {}),
});

/**
 * 發送 Teams Webhook 通知（fire-and-forget）
 * 查詢目標用戶的所有 active & subscribed webhook，逐一發送 Adaptive Card
 */
export const sendTeamsNotification = async ({ user_id, event_type, title, message, ticketId, targetEmail, url }) => {
  try {
    // 查用戶的所有啟用 webhooks
    const { data: webhooks } = await supabase
      .from('webhook_channels')
      .select('webhook_url, subscribed_events')
      .eq('scope_type', 'user')
      .eq('scope_id', user_id)
      .eq('is_active', true);

    if (!webhooks?.length) return;

    // 過濾已訂閱此事件的 webhook（subscribed_events 為 null 代表全部訂閱）
    const matched = webhooks.filter((w) =>
      !w.subscribed_events || w.subscribed_events.includes(event_type)
    );

    if (!matched.length) return;

    // 組 Adaptive Card & 發送(url 可覆寫,工作項目等非 Ticket 資源用)
    const mainSystemUrl = import.meta.env.VITE_MAIN_SYSTEM_URL || '';
    const ticketUrl = url || `${mainSystemUrl}/project/#/ticket?id=${ticketId}`;
    const card = buildTeamsCard({ title, message, url: ticketUrl, targetEmail });

    matched.forEach(({ webhook_url }) => {
      fetch(webhook_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(card),
      })
        .then(async (res) => {
          if (!res.ok) {
            const body = await res.text().catch(() => '');
            console.error(`[Teams webhook] HTTP ${res.status} for user ${user_id}:`, body);
          }
        })
        .catch((err) => console.error('[Teams webhook] Network error:', err));
    });
  } catch (error) {
    console.error('Error sending Teams notification:', error);
  }
};

/**
 * 發送 Email 通知（透過主系統 API）
 */
export const sendEmailNotification = async ({ to, subject, html, type }) => {
  try {
    const mainSystemUrl = import.meta.env.VITE_MAIN_SYSTEM_URL;
    if (!mainSystemUrl) {
      console.warn('MAIN_SYSTEM_URL not configured, skipping email');
      return { error: null };
    }

    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) {
      console.warn('No auth session, skipping email');
      return { error: null };
    }

    const response = await fetch(`${mainSystemUrl}/api/notifications/email`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ to, subject, html, type }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Email API error: ${response.status} ${text}`);
    }

    return { error: null };
  } catch (error) {
    console.error('Error sending email notification:', error);
    return { error };
  }
};
